import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { MainContainer } from "@components/templates/layouts/main-container/main-container";
import { RoundBorderComponentContainer } from "@components/templates/layouts/round-border-component-container/round-border-component-container";
import { GatewaysListComponent } from "./gateways-list-component";

import { updateGatewaysWithSourcesResultsReady } from "@src/reducers/your-reducer";
import { useGatewaysData } from "./hooks/use-gateways-data";
import { loadGatewaysWithSourcesListsData } from "@src/cloud-connect/use-cases/load-gateway-lists-data";

export const GatewaysAndSourcesListPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [isGatewayRefetched, setIsGatewayRefetched] = useState(false);

  const gatewaysData = useGatewaysData(true);
  const targetGatewayUuid =
    new URLSearchParams(location.search).get("gateway_uuid")?.trim() || "";

  useEffect(() => {
    return () => {
      dispatch(updateGatewaysWithSourcesResultsReady(false));
    };
  }, [dispatch]);

  useEffect(() => {
    let isCancelled = false;

    setIsGatewayRefetched(false);

    if (!targetGatewayUuid) {
      return;
    }

    const refetchGateways = async () => {
      await loadGatewaysWithSourcesListsData();

      if (!isCancelled) {
        setIsGatewayRefetched(true);
      }
    };

    refetchGateways();

    return () => {
      isCancelled = true;
    };
  }, [targetGatewayUuid]);

  useEffect(() => {
    if (!targetGatewayUuid) return;

    const clearHighlightTimeout = setTimeout(() => {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete("gateway_uuid");
      const nextSearch = searchParams.toString();
      navigate(
        {
          pathname: window.location.pathname,
          search: nextSearch ? `?${nextSearch}` : "",
        },
        { replace: true },
      );
    }, 30000);

    return () => clearTimeout(clearHighlightTimeout);
  }, [targetGatewayUuid, navigate]);

  useEffect(() => {
    if (
      !targetGatewayUuid ||
      !isGatewayRefetched ||
      !gatewaysData.gatewaysListsResultsReady
    ) {
      return;
    }

    let isCancelled = false;
    let attempts = 0;
    const maxAttempts = 20;

    const tryScrollToGateway = () => {
      if (isCancelled) return;

      const targetGatewayRow = document.getElementById(
        `gateway-row-${targetGatewayUuid}`,
      ) as HTMLTableRowElement | null;

      if (!targetGatewayRow) {
        attempts += 1;
        if (attempts < maxAttempts) {
          setTimeout(tryScrollToGateway, 100);
        }
        return;
      }

      targetGatewayRow.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    };

    tryScrollToGateway();

    return () => {
      isCancelled = true;
    };
  }, [
    targetGatewayUuid,
    isGatewayRefetched,
    gatewaysData.gatewaysListsResultsReady,
    location.pathname,
    navigate,
  ]);

  return (
    <MainContainer>
      <>
        <RoundBorderComponentContainer>
          <GatewaysListComponent viewToBeRendered={gatewaysData.viewState} />
        </RoundBorderComponentContainer>
      </>
    </MainContainer>
  );
};
